import type { AuthStepIndicator } from '../layout/StepDots';
import type { SignupSessionSnapshot } from './signupSession';

/**
 * The signup step machine.
 *
 * Every step is one `/signup` URL, so the browser's back button and a refresh both work, and
 * nothing about the flow lives only in React state. What the URL cannot carry — the proof
 * token, the typed details, the provider token waiting on a mailbox proof — lives in
 * `sessionStorage`, and this module is the single place that decides whether the step the URL
 * asks for is actually reachable with what is there.
 */

export const SIGNUP_STEPS = ['email', 'code', 'details', 'social-details', 'link-proof'] as const;

export type SignupStep = (typeof SIGNUP_STEPS)[number];

/** The three dots the approved prototype shows on the email-signup steps. */
const SIGNUP_STEP_TOTAL = 3;

export interface SignupStepRequest {
	/** The step the URL asks for. */
	step: SignupStep | undefined;
	/** The address the URL carries. Every step past the first needs one. */
	email: string | undefined;
	session: SignupSessionSnapshot;
	/** A social tap has already created the User and it has not finished its details. */
	incompleteSignup: boolean;
}

/**
 * Which step may actually render. A URL that asks for a step whose evidence is gone — a proof
 * token that was never minted, a link-proof handoff from a tab that has since been closed —
 * falls back to the first step rather than rendering a form that cannot submit.
 */
export const resolveSignupStep = (request: SignupStepRequest): SignupStep => {
	const { step, email, session, incompleteSignup } = request;

	switch (step) {
		case 'social-details':
			return incompleteSignup ? 'social-details' : 'email';
		case 'link-proof':
			return session.linkProof ? 'link-proof' : 'email';
		case 'details':
			// The proof token only has to exist here, not still be alive: an expired proof is a
			// state this step renders (details preserved, confirm the address again), not a reason
			// to throw the typed details away.
			if (email && session.proofToken) return 'details';
			return email && session.codeSentFor === email ? 'code' : 'email';
		case 'code':
			return email && session.codeSentFor === email ? 'code' : 'email';
		default:
			return 'email';
	}
};

/** Where the step sits on the three-dot indicator, or nothing for the screens outside the count. */
export const stepIndicatorFor = (step: SignupStep): AuthStepIndicator | undefined => {
	switch (step) {
		case 'email':
			return { index: 0, total: SIGNUP_STEP_TOTAL };
		case 'code':
			return { index: 1, total: SIGNUP_STEP_TOTAL };
		case 'details':
		case 'social-details':
			return { index: 2, total: SIGNUP_STEP_TOTAL };
		case 'link-proof':
			// Proof before a link is not a signup step: nobody is on their way to an account here.
			return undefined;
	}
};
